import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Space, Alert, Card, Table, Collapse, Switch } from 'antd';
import { SaveOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { baseUrl_SPB } from '../../../BaseUrl';
import axios from 'axios';

const ArrayFieldComponent = ({ title, items, onAdd, onRemove, onUpdate }) => (
  <Card title={title} size="small" style={{ marginBottom: 12 }}>
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      {items.map((item, index) => (
        <div key={index} style={{ display: 'flex', gap: 8 }}>
          <Input
            value={item}
            onChange={(e) => onUpdate(index, e.target.value)}
            placeholder={`Enter ${title.toLowerCase()}`}
            style={{ flex: 1 }}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onRemove(index)}
          />
        </div>
      ))}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={onAdd}
        block
      >
        Add {title.toLowerCase()}
      </Button>
    </Space>
  </Card>
);

const ProfessionalDetailsPanel = ({ employee, token, onUpdate }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [changed, setChanged] = useState(false);

  // Dynamic arrays
  const [skills, setSkills] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [recognitions, setRecognitions] = useState([]);
  const [professionalMemberships, setProfessionalMemberships] = useState([]);
  const [professionalExperience, setProfessionalExperience] = useState([]);

  useEffect(() => {
    if (employee) {
      form.setFieldsValue({
        // Job details
        jobTitle: employee.jobTitle || '',
        startDate: employee.startDate || '',
        email: employee.email || '',
        
        // Bank details
        bankName: employee.bankDetails?.[0]?.bankName || '',
        bsbNumber: employee.bankDetails?.[0]?.bsbNumber || '',
        accountNumber: employee.bankDetails?.[0]?.accountNumber || '',
        defaultAccount: employee.bankDetails?.[0]?.defaultAccount || false,
        
        // Tax & Residency
        taxFileNumber: employee.taxFileNumber || '',
        taxWithholdingStatus: employee.taxWithholdingStatus || '',
        residencyStatus: employee.residencyStatus || '',
        
        // Superannuation
        superannuationName: employee.superannuation?.superannuationName || '',
        superannuationFundABNNumber: employee.superannuation?.superannuationFundABNNumber || '',
        superannuationMembershipAccountNumber: employee.superannuation?.superannuationMembrshipAccountNumber || '',
        defaultFundSelectionStatus: employee.superannuation?.defaultFundSelectionStatus || false,
        defaultUSI: employee.superannuation?.defaultUSI || '',
      });

      // Load dynamic arrays
      setSkills(employee.skills || []);
      setQualifications(employee.qualifications || []);
      setCertifications(employee.certifications || []);
      setTrainings(employee.trainings || []);
      setRecognitions(employee.recognitions || []);
      setProfessionalMemberships(employee.professionalMemberships || []);
      setProfessionalExperience(employee.professionalExperience || []);
    }
  }, [employee, form]);

  // Reusable guard for saveuser
  function canSaveUser(payload) {
    const id = payload?.id || payload?.userId || payload?.employeeId;
    const role = payload?.role;
    return !!id && !!role;
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError('');

      const keepExisting = (nextValue, existingValue) => {
        if (nextValue === undefined || nextValue === null) return existingValue;
        if (typeof nextValue === 'string' && nextValue.trim() === '') return existingValue;
        return nextValue;
      };

      const nextBankDetails = {
        ...(employee?.bankDetails?.[0] || {}),
        bankName: keepExisting(values.bankName, employee?.bankDetails?.[0]?.bankName),
        bsbNumber: keepExisting(values.bsbNumber, employee?.bankDetails?.[0]?.bsbNumber),
        accountNumber: keepExisting(values.accountNumber, employee?.bankDetails?.[0]?.accountNumber),
        defaultAccount: values.defaultAccount ?? employee?.bankDetails?.[0]?.defaultAccount ?? false,
      };

      const nextSuper = {
        ...(employee?.superannuation || {}),
        superannuationName: keepExisting(values.superannuationName, employee?.superannuation?.superannuationName),
        superannuationFundABNNumber: keepExisting(values.superannuationFundABNNumber, employee?.superannuation?.superannuationFundABNNumber),
        superannuationMembrshipAccountNumber: keepExisting(
          values.superannuationMembershipAccountNumber,
          employee?.superannuation?.superannuationMembrshipAccountNumber
        ),
        defaultFundSelectionStatus: values.defaultFundSelectionStatus ?? employee?.superannuation?.defaultFundSelectionStatus ?? false,
        defaultUSI: keepExisting(values.defaultUSI, employee?.superannuation?.defaultUSI),
      };

      const payload = {
        ...employee,
        jobTitle: keepExisting(values.jobTitle, employee?.jobTitle),
        startDate: keepExisting(values.startDate, employee?.startDate),
        email: keepExisting(values.email, employee?.email),
        taxFileNumber: keepExisting(values.taxFileNumber, employee?.taxFileNumber),
        taxWithholdingStatus: keepExisting(values.taxWithholdingStatus, employee?.taxWithholdingStatus),
        residencyStatus: keepExisting(values.residencyStatus, employee?.residencyStatus),
        bankDetails: [nextBankDetails],
        superannuation: nextSuper,
        skills: skills.filter((s) => s.trim()),
        qualifications: qualifications.filter((q) => q.trim()),
        certifications: certifications.filter((c) => c.trim()),
        trainings: trainings.filter((t) => t.trim()),
        recognitions: recognitions.filter((r) => r.trim()),
        professionalMemberships: professionalMemberships.filter((m) => m.trim()),
        professionalExperience: professionalExperience.filter((pe) => pe.jobTitle?.trim()),
      };

      if (!canSaveUser(payload)) {
        setError('Cannot save: User ID or role is missing.');
        setSaving(false);
        return;
      }

      await axios.post(`${baseUrl_SPB}/api/v1/user/employmentdetails/save`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setChanged(false);
      onUpdate?.();
    } catch (err) {
      if (err?.errorFields) return;
      setError('Failed to save professional details.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const updateArrayField = (array, index, value, setter) => {
    const updated = [...array];
    updated[index] = value;
    setter(updated);
    setChanged(true);
  };

  const addArrayField = (array, setter) => {
    setter([...array, '']);
    setChanged(true);
  };

  const removeArrayField = (array, index, setter) => {
    setter(array.filter((_, i) => i !== index));
    setChanged(true);
  };

  const updateExperienceField = (index, field, value) => {
    const updated = [...professionalExperience];
    updated[index] = { ...updated[index], [field]: value };
    setProfessionalExperience(updated);
    setChanged(true);
  };

  const experienceColumns = [
    {
      title: 'Job Title',
      dataIndex: 'jobTitle',
      key: 'jobTitle',
      render: (text, _, index) => (
        <Input
          value={text}
          onChange={(e) => updateExperienceField(index, 'jobTitle', e.target.value)}
          placeholder="Job title"
          size="small"
        />
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'start',
      key: 'start',
      render: (text, _, index) => (
        <Input
          value={text}
          onChange={(e) => updateExperienceField(index, 'start', e.target.value)}
          placeholder="DD-MM-YYYY"
          size="small"
        />
      ),
    },
    {
      title: 'End Date',
      dataIndex: 'end',
      key: 'end',
      render: (text, _, index) => (
        <Input
          value={text}
          onChange={(e) => updateExperienceField(index, 'end', e.target.value)}
          placeholder="DD-MM-YYYY"
          size="small"
        />
      ),
    },
    {
      title: 'Total Years',
      dataIndex: 'totalYearsOfExperience',
      key: 'totalYearsOfExperience',
      render: (text, _, index) => (
        <Input
          type="number"
          value={text}
          onChange={(e) => updateExperienceField(index, 'totalYearsOfExperience', e.target.value)}
          placeholder="Years"
          size="small"
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, __, index) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => setProfessionalExperience(professionalExperience.filter((_, i) => i !== index))}
        />
      ),
    },
  ];

  const collapseItems = [
    {
      key: 'job-details',
      label: 'Job Details',
      children: (
        <Form form={form} layout="vertical" onValuesChange={() => setChanged(true)}>
          <Form.Item
            name="jobTitle"
            label="Job title"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="startDate"
            label="Start date"
          >
            <Input placeholder="DD-MM-YYYY" />
          </Form.Item>

          <Form.Item name="email" label="Email" >
            <Input disabled />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'bank',
      label: 'Bank Details',
      children: (
        <Form form={form} layout="vertical" onValuesChange={() => setChanged(true)}>
          <Form.Item name="bankName" label="Bank name">
            <Input />
          </Form.Item>

          <Form.Item name="bsbNumber" label="BSB number">
            <Input />
          </Form.Item>

          <Form.Item name="accountNumber" label="Account number">
            <Input />
          </Form.Item>

          <Form.Item name="defaultAccount" label="Default account" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'tax',
      label: 'Tax & Residency',
      children: (
        <Form form={form} layout="vertical" onValuesChange={() => setChanged(true)}>
          <Form.Item name="taxFileNumber" label="Tax file number">
            <Input />
          </Form.Item>

          <Form.Item name="taxWithholdingStatus" label="Tax withholding status">
            <Input />
          </Form.Item>

          <Form.Item name="residencyStatus" label="Residency status">
            <Input />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'super',
      label: 'Superannuation',
      children: (
        <Form form={form} layout="vertical" onValuesChange={() => setChanged(true)}>
          <Form.Item name="superannuationName" label="Superannuation fund name">
            <Input />
          </Form.Item>

          <Form.Item name="superannuationFundABNNumber" label="Fund ABN number">
            <Input />
          </Form.Item>

          <Form.Item name="superannuationMembershipAccountNumber" label="Membership/Account number">
            <Input />
          </Form.Item>

          <Form.Item name="defaultFundSelectionStatus" label="Default fund selection" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="defaultUSI" label="Default USI">
            <Input disabled />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'career',
      label: 'Career Details',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <ArrayFieldComponent
            title="Skills"
            items={skills}
            onAdd={() => addArrayField(skills, setSkills)}
            onRemove={(index) => removeArrayField(skills, index, setSkills)}
            onUpdate={(index, value) => updateArrayField(skills, index, value, setSkills)}
          />

          <ArrayFieldComponent
            title="Qualifications"
            items={qualifications}
            onAdd={() => addArrayField(qualifications, setQualifications)}
            onRemove={(index) => removeArrayField(qualifications, index, setQualifications)}
            onUpdate={(index, value) => updateArrayField(qualifications, index, value, setQualifications)}
          />

          <ArrayFieldComponent
            title="Certifications"
            items={certifications}
            onAdd={() => addArrayField(certifications, setCertifications)}
            onRemove={(index) => removeArrayField(certifications, index, setCertifications)}
            onUpdate={(index, value) => updateArrayField(certifications, index, value, setCertifications)}
          />

          <ArrayFieldComponent
            title="Trainings"
            items={trainings}
            onAdd={() => addArrayField(trainings, setTrainings)}
            onRemove={(index) => removeArrayField(trainings, index, setTrainings)}
            onUpdate={(index, value) => updateArrayField(trainings, index, value, setTrainings)}
          />

          <ArrayFieldComponent
            title="Recognitions"
            items={recognitions}
            onAdd={() => addArrayField(recognitions, setRecognitions)}
            onRemove={(index) => removeArrayField(recognitions, index, setRecognitions)}
            onUpdate={(index, value) => updateArrayField(recognitions, index, value, setRecognitions)}
          />

          <ArrayFieldComponent
            title="Professional memberships"
            items={professionalMemberships}
            onAdd={() => addArrayField(professionalMemberships, setProfessionalMemberships)}
            onRemove={(index) => removeArrayField(professionalMemberships, index, setProfessionalMemberships)}
            onUpdate={(index, value) => updateArrayField(professionalMemberships, index, value, setProfessionalMemberships)}
          />
        </Space>
      ),
    },
    {
      key: 'experience',
      label: 'Professional Experience',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {professionalExperience.length > 0 ? (
            <Table
              rowKey={(_, index) => index}
              dataSource={professionalExperience}
              columns={experienceColumns}
              pagination={false}
              size="small"
              scroll={{ x: 600 }}
            />
          ) : (
            <p style={{ color: '#999' }}>No professional experience recorded</p>
          )}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => {
              setProfessionalExperience([
                ...professionalExperience,
                { jobTitle: '', start: '', end: '', totalYearsOfExperience: '' },
              ]);
              setChanged(true);
            }}
            block
          >
            Add professional experience
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} />}

      <Collapse items={collapseItems} />

      {changed && (
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
          block
        >
          Save changes
        </Button>
      )}
    </Space>
  );
};

export default ProfessionalDetailsPanel;
